/**
 * 新規登録の下書き画像 (案件行がまだ無い状態) の画像枠セクション。
 * 画像は選んだ時点でストレージへ上げ (POST /products/upload-image・案件 ID 不要)、URL を下書きに持つ。
 * 名前・削除・並べ替えもクライアントの配列に対して行い、「登録する」で 1 回の POST にまとめる
 * (docs/features/manual-product-brand-first-flow.md §6.7)。新規登録ページと素材パネルの案件登録モーダルで共用する。
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../../../components/common/Button';
import { useUploadProductImage } from '../../../features/products/hooks';
import type { ProductDraftImages } from '../../../features/products/productDraft';
import { PRODUCT_IMAGE_ACCEPT, isUploadableProductImage } from '../../../features/products/imageFile';
import { useToast } from '../../../hooks/useToast';
import { getErrorMessage } from '../../../utils/getErrorMessage';
import { buildManualSlots, manualActionTooltips, parseManualSlotKey, type ManualSlotRef } from '../manualProductSlots';
import { ProductImageSlotsSection } from './ProductImageSlotsSection';
import { LocalImagePreview } from './LocalImagePreview';
import { useProductImageDialogs } from './useProductImageDialogs';
import { isSameOrder, type ReorderItem } from './imageSlotReorder';
import { toReorderItem, type ProductImageSlot } from './productImageSlots';
import { removeDraftImage, renameDraftImage, reorderDraftImages, setDraftImage } from './draftProductImages';

interface Props {
  images: ProductDraftImages;
  /**
   * 下書き画像の更新 (関数型)。アップロードは並行して完了しうるので、開始時点の一覧ではなく最新の一覧に対して反映する
   * (別々の枠へ続けて入れた画像が、後から完了した処理で消えないように)
   */
  onChange: (update: (prev: ProductDraftImages) => ProductDraftImages) => void;
  /** 商品名 (メイン画像の alt) */
  title?: string;
  /** 登録中など操作を止めるとき */
  disabled?: boolean;
  /** アップロード中かどうかが変わったとき (登録ボタンを止めるのに使う) */
  onUploadingChange?: (uploading: boolean) => void;
}

export function DraftProductImagesSection({ images, onChange, title, disabled, onUploadingChange }: Props) {
  const toast = useToast();
  const uploadImage = useUploadProductImage();
  const slots: ProductImageSlot[] = buildManualSlots({ ...images, title });
  const toRef = (slot: ProductImageSlot): ManualSlotRef => parseManualSlotKey(slot.key) ?? { type: 'main' };

  // 画像の設定モーダル (ローカル / URL)
  const [uploadSlot, setUploadSlot] = useState<ManualSlotRef | null>(null);
  const [uploadMode, setUploadMode] = useState<'local' | 'url'>('local');
  const [uploadUrl, setUploadUrl] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  // 進行中のアップロード件数 (並行する)。0 件になるまで登録ボタンを止める
  const [uploadingCount, setUploadingCount] = useState(0);
  const isUploading = uploadingCount > 0;
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [reorderItems, setReorderItems] = useState<ReorderItem[] | null>(null);
  const onUploadingChangeRef = useRef(onUploadingChange);
  onUploadingChangeRef.current = onUploadingChange;
  useEffect(() => {
    onUploadingChangeRef.current?.(uploadingCount > 0);
  }, [uploadingCount]);

  const openUploadModal = (slot: ManualSlotRef) => {
    setUploadSlot(slot);
    setUploadMode('local');
    setUploadUrl('');
    setUploadFile(null);
    setUploadPreview(null);
  };

  const closeUploadModal = () => {
    setUploadSlot(null);
    setUploadFile(null);
    setUploadPreview(null);
    setUploadUrl('');
  };

  const processFile = (file: File) => {
    if (!isUploadableProductImage(file)) {
      toast.error('画像ファイルを選択してください');
      return;
    }
    setUploadFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setUploadPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  /** ファイルをストレージへ上げて枠に入れる (枠へのドロップとモーダルの確定で共用) */
  const uploadToSlot = async (slot: ManualSlotRef, file: File) => {
    if (!isUploadableProductImage(file)) {
      toast.error('画像ファイルを選択してください');
      return;
    }
    setUploadingCount((n) => n + 1);
    try {
      const result = await uploadImage.mutateAsync(file);
      onChange((prev) => setDraftImage(prev, slot, { url: result.url, original_path: result.original_path ?? null }));
      toast.success('画像を設定しました');
      closeUploadModal();
    } catch (err) {
      toast.error(`画像のアップロードに失敗しました: ${getErrorMessage(err)}`);
    } finally {
      setUploadingCount((n) => n - 1);
    }
  };

  const handleUploadConfirm = async () => {
    if (!uploadSlot) return;
    if (uploadMode === 'local') {
      if (!uploadFile) { toast.error('ファイルを選択してください'); return; }
      await uploadToSlot(uploadSlot, uploadFile);
      return;
    }
    const url = uploadUrl.trim();
    if (!url) { toast.error('URLを入力してください'); return; }
    onChange((prev) => setDraftImage(prev, uploadSlot, { url }));
    closeUploadModal();
  };

  const dialogs = useProductImageDialogs({
    replaceTitle: (slot) => `${slot.label}を差し替えますか？`,
    onConfirmReplace: async () => { /* 下書きでは差替は使わない (削除して入れ直す) */ },
    onSaveName: async (slot, name) => {
      onChange((prev) => renameDraftImage(prev, toRef(slot), name));
    },
  });

  const currentReorderItems = (): ReorderItem[] => slots.map(toReorderItem);
  const reorderDirty = !!reorderItems && !isSameOrder(currentReorderItems(), reorderItems);
  const reorderDisabledReason = disabled ? '登録中は並べ替えできません' : !slots.some((s) => !!s.url) ? '並べ替えできる画像がありません' : null;

  return (
    <>
      <ProductImageSlotsSection
        slots={slots}
        uniformLayout
        capabilities={{
          select: false,
          save: false,
          replace: false,
          delete: true,
          editName: true,
          fileDrop: true,
          emptyClick: true,
          reorder: true,
        }}
        deleteButton="trash"
        actionTooltips={manualActionTooltips}
        isImageSelected={() => false}
        onSelectImage={() => undefined}
        onReplace={() => undefined}
        onDelete={(slot) => onChange((prev) => removeDraftImage(prev, toRef(slot)))}
        onEditName={dialogs.openNameEdit}
        onEmptyClick={(slot) => { if (!disabled) openUploadModal(toRef(slot)); }}
        onDropFile={(slot, file) => { if (!disabled) void uploadToSlot(toRef(slot), file); }}
        saveButtons={{
          isSavedCloud: () => false,
          isSavedTemp: () => false,
          pendingCloud: false,
          pendingTemp: false,
          onSaveCloud: () => undefined,
          onSaveTemp: () => undefined,
        }}
        replaceDisabled={disabled}
        noImageFallback
        reorder={{
          disabledReason: reorderDisabledReason,
          items: reorderItems,
          labelAt: (index) => (index === 0 ? 'メイン画像' : `画像 ${index}`),
          isDirty: reorderDirty,
          isSubmitting: false,
          submitLabel: '保存中...',
          hint: '画像カードのドラッグで並び替え',
          onStart: () => { if (reorderDisabledReason === null) setReorderItems(currentReorderItems()); },
          onCancel: () => setReorderItems(null),
          onChange: setReorderItems,
          onComplete: () => {
            if (reorderItems && reorderDirty) onChange((prev) => reorderDraftImages(prev, reorderItems));
            setReorderItems(null);
          },
        }}
      />
      {dialogs.dialogs}

      {uploadSlot && createPortal(
        <div className="image-upload-modal-backdrop" onClick={closeUploadModal}>
          <div className="image-upload-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="画像を設定">
            <div className="image-upload-modal-header">
              <h3>画像を設定</h3>
              <button className="modal-close-button" onClick={closeUploadModal} type="button" aria-label="閉じる">×</button>
            </div>
            <div className="image-upload-modal-body">
              <div className="upload-mode-radio">
                <label className="radio-label">
                  <input type="radio" name="draftUploadMode" value="local" checked={uploadMode === 'local'} onChange={() => setUploadMode('local')} />
                  ローカルからアップロード
                </label>
                <label className="radio-label">
                  <input type="radio" name="draftUploadMode" value="url" checked={uploadMode === 'url'} onChange={() => setUploadMode('url')} />
                  URLを指定
                </label>
              </div>
              {uploadMode === 'local' ? (
                <div className="upload-local-section">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={PRODUCT_IMAGE_ACCEPT}
                    style={{ display: 'none' }}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) processFile(f); }}
                    data-testid="draft-image-file"
                  />
                  {uploadPreview ? (
                    <div
                      className="upload-preview"
                      onClick={() => fileInputRef.current?.click()}
                      onDrop={(e) => { e.preventDefault(); setIsDragging(false); const f = e.dataTransfer.files?.[0]; if (f) processFile(f); }}
                      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                      onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                    >
                      <LocalImagePreview key={uploadPreview} src={uploadPreview} fileName={uploadFile?.name} />
                    </div>
                  ) : (
                    <div
                      className={`upload-drop-area${isDragging ? ' dragging' : ''}`}
                      onClick={() => fileInputRef.current?.click()}
                      onDrop={(e) => { e.preventDefault(); setIsDragging(false); const f = e.dataTransfer.files?.[0]; if (f) processFile(f); }}
                      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                      onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                    >
                      <span className="upload-icon">+</span>
                      <span>クリックまたはドラッグ&ドロップで画像を選択</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="upload-url-section">
                  <label>画像URL</label>
                  <input type="text" value={uploadUrl} onChange={(e) => setUploadUrl(e.target.value)} placeholder="https://example.com/image.jpg" aria-label="画像URL" />
                  {uploadUrl.trim() && (
                    <div className="upload-preview">
                      <img src={uploadUrl} alt="プレビュー" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="image-upload-modal-footer">
              <Button variant="danger" size="md" onClick={closeUploadModal}>キャンセル</Button>
              <Button
                variant="primary"
                size="md"
                onClick={() => void handleUploadConfirm()}
                disabled={isUploading || (uploadMode === 'local' ? !uploadFile : !uploadUrl.trim())}
              >
                {isUploading ? '設定中...' : '設定'}
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
